/**
 * Player jet (GDD §3.6: twin-engine delta with canards and twin canted fins, gold-tinted canopy, panel lines,
 * amber stripes, underwing missiles, red/green tip lights, blinking tail strobe, flickering afterburners).
 * Also the enemy jet with the dark material set. Ported verbatim (concept L370–L426).
 */
import {
  CircleGeometry,
  ConeGeometry,
  CylinderGeometry,
  ExtrudeGeometry,
  Group,
  LatheGeometry,
  Mesh,
  Shape,
  SphereGeometry,
  Sprite,
  TorusGeometry,
  Vector2,
  BoxGeometry,
} from 'three';
import { conceptCapsule, mesh } from '../../world/geometry.ts';
import type { Materials } from '../materials.ts';

const TAU = Math.PI * 2;

export interface JetParts {
  body: Group;
  flames: Mesh[];
  glow: Sprite;
  strobe: Mesh;
  navR: Mesh;
  navG: Mesh;
}

export function buildJet(M: Materials): Group & { userData: JetParts } {
  const root = new Group();
  const body = new Group();
  root.add(body);
  const prof = (
    [
      [0.95, -7.9],
      [1.05, -7.4],
      [1.3, -6],
      [1.45, -4],
      [1.5, -1.5],
      [1.45, 1.0],
      [1.3, 3.4],
      [1.05, 5.6],
      [0.7, 7.4],
      [0.28, 8.9],
      [0.02, 9.6],
    ] as const
  ).map((p) => new Vector2(p[0], p[1]));
  const fus = new LatheGeometry(prof, 32);
  fus.rotateX(-Math.PI / 2);
  fus.scale(1, 0.82, 1);
  mesh(fus, M.fuselage, 0, 0, 0, body);
  const spine = conceptCapsule(0.55, 7, 6, 14);
  spine.rotateX(Math.PI / 2);
  spine.scale(1, 0.7, 1);
  mesh(spine, M.fuselage, 0, 0.78, 1.4, body);
  const can = new SphereGeometry(1, 32, 16, 0, TAU, 0, Math.PI / 2);
  can.scale(0.7, 0.72, 2.5);
  mesh(can, M.glass, 0, 0.66, -4.3, body);
  const frame = new TorusGeometry(0.7, 0.05, 6, 24, Math.PI);
  frame.scale(1, 1.03, 1);
  mesh(frame, M.dark, 0, 0.66, -3.4, body);
  const wing = (sign: number, pts: readonly (readonly [number, number])[], y: number, depth: number) => {
    const s = new Shape();
    const [p0] = pts;
    if (!p0) throw new Error('wing needs points');
    s.moveTo(p0[0] * sign, p0[1]);
    for (let i = 1; i < pts.length; i++) {
      const p = pts[i];
      if (p) s.lineTo(p[0] * sign, p[1]);
    }
    s.closePath();
    const g = new ExtrudeGeometry(s, {
      depth,
      bevelEnabled: true,
      bevelThickness: 0.06,
      bevelSize: 0.07,
      bevelSegments: 2,
    });
    g.rotateX(Math.PI / 2);
    g.translate(0, y + depth / 2, 0);
    return g;
  };
  const wp = [
    [1.0, -3.4],
    [8.7, 3.0],
    [8.9, 4.4],
    [1.0, 5.5],
  ] as const;
  const sp = [
    [1.0, 5.4],
    [3.9, 7.2],
    [4.0, 8.2],
    [1.0, 7.9],
  ] as const;
  const cp = [
    [1.2, -5.6],
    [2.9, -4.4],
    [2.9, -3.9],
    [1.2, -4.1],
  ] as const;
  for (const sg of [1, -1]) {
    mesh(wing(sg, wp, -0.28, 0.2), M.wing, 0, 0, 0, body);
    mesh(wing(sg, sp, -0.02, 0.14), M.wing, 0, 0, 0, body);
    mesh(wing(sg, cp, 0.1, 0.1), M.wing, 0, 0, 0, body);
    const fs = new Shape();
    fs.moveTo(3.4, 0.4);
    fs.lineTo(6.4, 3.7);
    fs.lineTo(7.7, 3.75);
    fs.lineTo(7.9, 0.4);
    fs.closePath();
    const fg = new ExtrudeGeometry(fs, {
      depth: 0.16,
      bevelEnabled: true,
      bevelThickness: 0.04,
      bevelSize: 0.05,
      bevelSegments: 1,
    });
    fg.rotateY(-Math.PI / 2);
    fg.translate(0.08, 0, 0);
    const fin = new Group();
    fin.position.set(sg * 1.0, 0.72, 0);
    fin.rotation.z = -sg * 0.3;
    body.add(fin);
    mesh(fg, M.wing, 0, 0, 0, fin);
    const stripe = new BoxGeometry(0.2, 0.34, 1.0);
    mesh(stripe, M.accent, 0, 3.2, 7.1, fin);
    mesh(new BoxGeometry(0.95, 1.05, 4.2), M.fuselage, sg * 1.5, -0.25, -0.6, body);
    mesh(new BoxGeometry(0.8, 0.85, 0.12), M.dark, sg * 1.5, -0.25, -2.74, body, false);
    const noz = new CylinderGeometry(0.64, 0.54, 1.6, 24, 1, true);
    noz.rotateX(Math.PI / 2);
    mesh(noz, M.nozzle, sg * 0.62, -0.05, 8.1, body);
    const core = new CircleGeometry(0.5, 20);
    mesh(core, M.core, sg * 0.62, -0.05, 8.7, body, false);
    const mis = new CylinderGeometry(0.15, 0.15, 3.2, 10);
    mis.rotateX(Math.PI / 2);
    mesh(mis, M.missile, sg * 4.3, -0.72, 1.6, body);
    const tip = new ConeGeometry(0.15, 0.5, 10);
    tip.rotateX(-Math.PI / 2);
    mesh(tip, M.red, sg * 4.3, -0.72, -0.25, body);
    mesh(new BoxGeometry(0.12, 0.42, 1.6), M.dark, sg * 4.3, -0.42, 1.6, body);
    mesh(new BoxGeometry(0.6, 0.06, 0.9), M.accent, sg * 6.2, -0.16, 2.9, body, false);
  }
  const pitot = new CylinderGeometry(0.03, 0.04, 1.6, 6);
  pitot.rotateX(Math.PI / 2);
  mesh(pitot, M.dark, 0, 0, -10.2, body);
  const navR = mesh(new SphereGeometry(0.14, 10, 8), M.navR, -8.8, -0.18, 3.8, body, false);
  const navG = mesh(new SphereGeometry(0.14, 10, 8), M.navG, 8.8, -0.18, 3.8, body, false);
  const strobe = mesh(new SphereGeometry(0.12, 10, 8), M.strobe, 0, 1.0, 7.4, body, false);
  const flames: Mesh[] = [];
  for (const sg of [1, -1]) {
    const o = new ConeGeometry(0.5, 4.6, 18, 1, true);
    o.rotateX(Math.PI / 2);
    o.translate(0, 0, 2.3);
    const fo = mesh(o, M.flameOuter, sg * 0.62, -0.05, 8.8, body, false);
    const inr = new ConeGeometry(0.3, 2.6, 16, 1, true);
    inr.rotateX(Math.PI / 2);
    inr.translate(0, 0, 1.3);
    const fi = mesh(inr, M.flameInner, sg * 0.62, -0.05, 8.8, body, false);
    flames.push(fo, fi);
  }
  const glow = new Sprite(M.glow);
  glow.scale.set(5, 5, 1);
  glow.position.set(0, -0.05, 9.4);
  body.add(glow);
  root.userData = { body, flames, glow, strobe, navR, navG } satisfies JetParts;
  return root as Group & { userData: JetParts };
}
